import { useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Download,
  Filter,
  Mail,
  MailX,
  Sun,
  Moon,
  Clock,
  CalendarDays,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { EmailSubscription } from "@/types/index";

export type SortField = "email" | "createdAt";
export type SortOrder = "asc" | "desc";

interface SubscriberTableProps {
  subscribers: EmailSubscription[];
  isLoading: boolean;
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
  page: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSearch: (query: string) => void;
  onStatusFilter: (status: string) => void;
  onPreferenceFilter: (preference: string) => void;
  onExport: () => void;
  statusFilter: string;
  preferenceFilter: string;
  searchQuery: string;
}

interface StatsCardsProps {
  totalSubscribers: number;
  activeSubscribers: number;
  unsubscribed: number;
  isLoading: boolean;
}

const preferenceIcons: Record<string, React.ReactNode> = {
  morning: <Sun className="w-3.5 h-3.5" />,
  afternoon: <Sun className="w-3.5 h-3.5" />,
  evening: <Moon className="w-3.5 h-3.5" />,
  nightly: <Moon className="w-3.5 h-3.5" />,
  weekly: <CalendarDays className="w-3.5 h-3.5" />,
};

const preferenceColors: Record<string, string> = {
  morning: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  afternoon: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
  evening: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
  nightly: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  weekly: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
};

// Stats Cards Component
export function SubscriberStatsCards({
  totalSubscribers,
  activeSubscribers,
  unsubscribed,
  isLoading,
}: StatsCardsProps) {
  const stats = [
    {
      label: "Total Subscribers",
      value: totalSubscribers,
      icon: Mail,
      color: "bg-blue-500/10 text-blue-500",
    },
    {
      label: "Active",
      value: activeSubscribers,
      icon: Mail,
      color: "bg-emerald-500/10 text-emerald-500",
    },
    {
      label: "Unsubscribed",
      value: unsubscribed,
      icon: MailX,
      color: "bg-red-500/10 text-red-400",
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {stats.map((_, i) => (
          <Card key={i} className="p-4">
            <div className="flex items-center gap-4">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      {stats.map((stat, index) => (
        <motion.div
          key={stat.label}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          <Card className="p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className={cn("p-2.5 rounded-xl", stat.color)}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums">
                  {stat.value.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          </Card>
        </motion.div>
      ))}
    </div>
  );
}

// Sortable Header Component
interface SortableHeaderProps {
  field: SortField;
  label: string;
  currentField: SortField;
  currentOrder: SortOrder;
  onSort: (field: SortField) => void;
  className?: string;
}

function SortableHeader({
  field,
  label,
  currentField,
  currentOrder,
  onSort,
  className,
}: SortableHeaderProps) {
  const isActive = currentField === field;

  return (
    <TableHead className={className}>
      <button
        onClick={() => onSort(field)}
        className={cn(
          "flex items-center gap-1 hover:text-foreground transition-colors",
          isActive && "text-foreground"
        )}
      >
        {label}
        {isActive ? (
          currentOrder === "asc" ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
        )}
      </button>
    </TableHead>
  );
}

// Loading Skeleton Row
function TableRowSkeleton() {
  return (
    <TableRow>
      <TableCell>
        <Skeleton className="h-4 w-48" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-5 w-20 rounded-full" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-5 w-20 rounded-full" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-4 w-24" />
      </TableCell>
    </TableRow>
  );
}

export function SubscriberTable({
  subscribers,
  isLoading,
  sortField,
  sortOrder,
  onSort,
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onStatusFilter,
  onPreferenceFilter,
  onExport,
  statusFilter,
  preferenceFilter,
  searchQuery,
}: SubscriberTableProps) {
  const [localSearch, setLocalSearch] = useState(searchQuery);

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  const handleSearch = (value: string) => {
    setLocalSearch(value);
    onSearch(value);
  };

  return (
    <div className="space-y-4">
      {/* Filters Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by email..."
              value={localSearch}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={statusFilter} onValueChange={onStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="subscribed">Subscribed</SelectItem>
              <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={preferenceFilter} onValueChange={onPreferenceFilter}>
            <SelectTrigger className="w-[140px]">
              <Clock className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Preference" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Preferences</SelectItem>
              <SelectItem value="morning">Morning</SelectItem>
              <SelectItem value="afternoon">Afternoon</SelectItem>
              <SelectItem value="evening">Evening</SelectItem>
              <SelectItem value="nightly">Nightly</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button variant="outline" onClick={onExport} className="gap-2">
          <Download className="w-4 h-4" />
          Export CSV
        </Button>
      </div>

      {/* Table */}
      <Card className="overflow-hidden border-border/50">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <SortableHeader
                field="email"
                label="Email"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={onSort}
              />
              <TableHead className="w-[120px]">Status</TableHead>
              <TableHead className="w-[140px]">Preference</TableHead>
              <SortableHeader
                field="createdAt"
                label="Joined"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={onSort}
                className="w-[140px]"
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: pageSize }).map((_, i) => (
                <TableRowSkeleton key={i} />
              ))
            ) : subscribers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-32 text-center">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Mail className="w-8 h-8 opacity-50" />
                    <span>No subscribers found</span>
                    <span className="text-sm">
                      Try adjusting your search or filters
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              subscribers.map((subscriber, index) => (
                <motion.tr
                  key={subscriber.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.03 }}
                  className="border-b transition-colors hover:bg-muted/30"
                >
                  <TableCell className="font-medium">
                    {subscriber.email}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={subscriber.isSubscribed ? "default" : "secondary"}
                      className={cn(
                        subscriber.isSubscribed
                          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {subscriber.isSubscribed ? "Subscribed" : "Unsubscribed"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {subscriber.briefPreference ? (
                      <div
                        className={cn(
                          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
                          preferenceColors[subscriber.briefPreference] ||
                            "bg-muted text-muted-foreground"
                        )}
                      >
                        {preferenceIcons[subscriber.briefPreference]}
                        <span className="capitalize">
                          {subscriber.briefPreference}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(subscriber.createdAt), "MMM d, yyyy")}
                  </TableCell>
                </motion.tr>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>
            Showing {totalItems > 0 ? startItem : 0}-{endItem} of {totalItems}{" "}
            subscribers
          </span>
          <span className="text-muted-foreground/50">|</span>
          <div className="flex items-center gap-2">
            <span>Per page:</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(v) => onPageSizeChange(parseInt(v, 10))}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={page === pageNum ? "default" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
